import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { API_BASE, api, type LandingFacts } from '../lib/api'
import { useQuery } from '@tanstack/react-query'
import { Logo } from '../components/Logo'
import { typeOf } from '../components/Page'
import { useSession } from '../lib/session'

/**
 * The public landing page.
 *
 * It answers one question in the first screen: can this thing tell me whether
 * the paper wrote about my subject, and show me the page? Everything below
 * that is evidence for the answer rather than a feature list.
 *
 * The figures are real. They come from `/api/landing`, which counts what has
 * actually been ingested. A landing page that advertises 498,116 pages over an
 * archive holding two editions is the first lie a visitor catches, and this
 * product's entire pitch is that it does not overstate what it holds.
 */
export function LandingPage() {
  const { config } = useSession()
  const facts = useQuery({
    queryKey: ['landing'],
    queryFn: () => api.get<LandingFacts>('/landing'),
    staleTime: 10 * 60 * 1000,
  })
  const f = facts.data

  return (
    <div className="min-h-full bg-paper text-body">
      <LandingNav />

      <div className="mx-auto max-w-[1200px] px-[--edge]">
        {/* ── the ask ───────────────────────────────────────────────── */}
        <section className="pb-[calc(2*var(--leading))] pt-[calc(3.5*var(--leading))]">
          <h1 className="-ml-[0.035em] text-[clamp(48px,7.4vw,104px)] font-semibold leading-[1.02] tracking-[-0.02em]">
            <span className="block">Did this paper</span>
            <span className="block">write about it?</span>
            <span className="block text-ink2">Show me on the page.</span>
          </h1>
          <p className="mt-[--leading] max-w-[58ch] text-[17px] leading-[--leading] text-ink2">
            A licensed research platform over digitised newspaper archives. Every article,
            photograph, advertisement and notice is extracted with the position it occupied
            on the page it was printed on, so a result is never a claim, it is the page.
          </p>

          <SearchBox />

          <div className="ident mt-[--half] uppercase tracking-[0.08em]">
            Searching as a guest returns headlines, dates and page references. The page itself
            needs a library sign-in.
          </div>
        </section>

        {/* ── what is actually held ─────────────────────────────────── */}
        <section id="coverage" className="pb-[calc(2*var(--leading))]">
          {/* The thick-thin rule pair around a dateline rail: the one place
              this system prints a rule, as front-page furniture. */}
          <hr className="m-0 h-[5px] border-0 border-b border-t-2 border-ink" />
          <div className="flex flex-wrap justify-between gap-[--half] gap-x-[--leading] py-[--half] text-[13px] uppercase leading-[--half] tracking-[0.08em] text-ink2">
            <div>{f?.publication ?? '-'}</div>
            <div>{f?.place ?? '-'}</div>
            <div>{f ? f.year_range : '-'}</div>
            <div>{f ? `${f.editions.toLocaleString()} editions` : '-'}</div>
            <div>{f ? `${f.pages.toLocaleString()} pages` : '-'}</div>
            <div>{f?.institution ?? '-'}</div>
          </div>
          <hr className="m-0 h-0 border-0 border-t border-ink" />
          <div className="grid grid-cols-1 gap-[--half] gap-x-[calc(2.5*var(--leading))] pb-[--leading] pt-[--half] sm:grid-cols-2">
            {INDEX.map(([label, num, lead], i) => (
              <p
                key={num}
                className="m-0 flex items-baseline gap-2 text-[15.5px] leading-[--leading]"
              >
                <a href={`#${SECTION_OF[i]}`} className="text-body no-underline hover:text-accent">
                  {label}
                </a>
                <span className="mb-[0.34em] min-w-[28px] flex-1 self-end border-b border-dotted border-rule" />
                <span
                  className={`text-[17px] font-semibold ${lead ? 'text-accent' : 'text-body'}`}
                >
                  {num}
                </span>
              </p>
            ))}
          </div>
        </section>

        {/* ── the three principles ──────────────────────────────────── */}
        <section id="how" className="pb-[calc(2.5*var(--leading))] pt-[calc(1.5*var(--leading))]">
          <div className="mb-[--half] text-[13px] uppercase leading-[--half] tracking-[0.08em] text-ink2">
            How it works
          </div>
          <h2 className="max-w-[34ch] text-[clamp(34px,4.2vw,58px)] leading-[1.06] tracking-[-0.015em]">
            Three principles, and you can check every one of them against the page.
          </h2>
          <div className="mt-[calc(1.5*var(--leading))] grid grid-cols-1 gap-[calc(1.5*var(--leading))] gap-x-[clamp(28px,4vw,64px)] md:grid-cols-3">
            {FEATURES.map(([title, body]) => (
              <div key={title}>
                <h3 className="text-[24px] leading-[--leading] tracking-[-0.01em]">{title}</h3>
                <p className="mt-[--half] text-justify text-[15.5px] leading-[--leading] text-ink2 hyphens-auto">
                  {body}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* ── the page in situ ──────────────────────────────────────── */}
        <PageInSitu sample={f?.sample} />

        {/* ── what access gives you ─────────────────────────────────── */}
        <section id="access" className="pb-[calc(2.5*var(--leading))]">
          <hr className="m-0 mb-[--half] h-0 border-0 border-t border-ink" />
          <div className="mb-[--leading] text-[13px] uppercase leading-[--half] tracking-[0.08em] text-ink2">
            What access gives you
          </div>
          <div className="grid grid-cols-1 gap-[calc(1.5*var(--leading))] gap-x-[clamp(28px,4vw,64px)] md:grid-cols-3">
            {TIERS.map((tier) => (
              <div key={tier.name}>
                <div className="mb-[--half] flex items-baseline gap-2.5">
                  <h3 className="text-[24px] leading-[1.1] tracking-[-0.01em]">{tier.name}</h3>
                  <div className="ident uppercase tracking-[0.08em]">{tier.sub}</div>
                </div>
                <div className="flex flex-col gap-[7px]">
                  {tier.gets.map((g) => (
                    <div key={g} className="flex gap-2.5 text-[15.5px] leading-snug">
                      <span className="mt-[7px] h-[9px] w-[9px] flex-none bg-accent" />
                      <span>{g}</span>
                    </div>
                  ))}
                  {tier.not.map((g) => (
                    <div key={g} className="flex gap-2.5 text-[15.5px] leading-snug text-ink3">
                      <span className="mt-[7px] h-[9px] w-[9px] flex-none border border-rule2" />
                      <span className="line-through decoration-[color:var(--color-neutral-400)]">
                        {g}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── for libraries ─────────────────────────────────────────── */}
        <section id="libraries" className="pb-[calc(2*var(--leading))]">
          <div className="grid grid-cols-1 items-start gap-[clamp(28px,4vw,64px)] md:grid-cols-2">
            <div>
              <div className="mb-[--half] text-[13px] uppercase leading-[--half] tracking-[0.08em] text-ink2">
                For libraries
              </div>
              <h3 className="mb-[--leading] max-w-[26ch] text-[clamp(24px,2.6vw,34px)] leading-[1.14] tracking-[-0.012em]">
                Licensed per publication and per institution, with the limits written into the licence.
              </h3>
              <p className="mb-[--leading] max-w-[48ch] text-justify text-[16px] leading-[--leading] text-ink2 hyphens-auto">
                User limits, date and section scope, trials and download rights are configured per
                licence and enforced in the database rather than in the interface.
              </p>
              <div className="flex flex-wrap gap-3">
                <a href={`mailto:${config?.support_email ?? ''}`} className="btn-primary no-underline">
                  Request a trial
                </a>
                <a href={`mailto:${config?.support_email ?? ''}`} className="btn-secondary no-underline">
                  Talk to us about a title
                </a>
              </div>
            </div>
            <div className="pt-[calc(var(--leading)+var(--half))]">
              {FACTS.map(([label, value]) => (
                <div
                  key={label}
                  className="flex items-baseline gap-[--leading] border-t border-rule py-[11px]"
                >
                  <div className="ident w-[108px] flex-none uppercase tracking-[0.08em]">
                    {label}
                  </div>
                  <div className="text-[15.5px] leading-snug">{value}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <LandingFooter />
      </div>
    </div>
  )
}

/** The sticky nav. Glass over the paper, never a solid bar. */
export function LandingNav() {
  return (
    <div
      className="sticky top-0 z-40 flex h-16 items-center gap-[clamp(14px,2.2vw,28px)] border-b border-rule px-[max(var(--edge),calc((100%-1200px)/2+var(--edge)))]"
      style={{
        background: 'var(--jl-glass)',
        backdropFilter: 'blur(18px) saturate(1.35)',
        WebkitBackdropFilter: 'blur(18px) saturate(1.35)',
      }}
    >
      <Link to="/" className="flex items-center gap-2.5 text-body no-underline">
        <Logo size={22} />
        <div className="whitespace-nowrap text-[17px] font-semibold tracking-[-0.005em]">
          <BrandName />
        </div>
      </Link>
      <div className="ml-auto hidden items-center gap-[clamp(12px,1.8vw,28px)] whitespace-nowrap text-[13px] uppercase tracking-[0.08em] lg:flex">
        <a href="#coverage">Coverage</a>
        <a href="#how">How it works</a>
        <a href="#access">Access</a>
        <a href="#libraries">For libraries</a>
        {/* The handbook is a separate static site, so a plain anchor: a
            client-side link would be matched against the router and land on
            the fallback. */}
        <a href="/docs/handbook/">Handbook</a>
      </div>
      <div className="ml-auto flex items-center gap-3 lg:ml-0">
        <Link to="/login" className="whitespace-nowrap text-[14px]">
          Sign in
        </Link>
        {/* Off a phone's bar: the search box is the first thing under it. */}
        <Link to="/search" className="btn-primary hidden whitespace-nowrap no-underline sm:inline-flex">
          Search the archive
        </Link>
      </div>
    </div>
  )
}

function BrandName() {
  const { config } = useSession()
  return <>{config?.product_name ?? ''}</>
}

/**
 * The hero search box. It is a real search: a guest may search and gets the
 * metadata tier back, which is the whole argument for a discovery tier and
 * costs no seat.
 */
function SearchBox() {
  const [q, setQ] = useState('')
  const navigate = useNavigate()
  return (
    <form
      onSubmit={(event) => {
        event.preventDefault()
        navigate(`/search?q=${encodeURIComponent(q)}`)
      }}
      className="mt-[--leading] flex max-w-[640px] items-center gap-3"
    >
      <label className="sr-only" htmlFor="landing-q">
        Search the archive
      </label>
      <input
        id="landing-q"
        name="q"
        value={q}
        onChange={(event) => setQ(event.target.value)}
        placeholder="Ask in your own words"
        className="input h-[52px] flex-1 text-[17px]"
      />
      <button type="submit" className="btn-primary h-[52px] whitespace-nowrap px-6 text-[15px]">
        Search
      </button>
    </form>
  )
}

/**
 * A real front page with its real regions drawn over it, which is the single
 * most convincing thing the product can put in front of a visitor. If nothing
 * has been ingested there is nothing honest to show, so the block is omitted
 * rather than faked.
 */
function PageInSitu({ sample }: { sample: LandingFacts['sample'] | undefined }) {
  if (!sample) return null
  return (
    <section className="pb-[calc(2.5*var(--leading))]">
      <div className="grid grid-cols-1 items-start gap-[clamp(28px,4vw,64px)] lg:grid-cols-[1.15fr_1fr]">
        <div>
          <div className="rounded-lg bg-well p-8 shadow-lg">
            <div className="relative">
              <img
                src={`${API_BASE}/${sample.image_url}`}
                alt={`Page ${sample.page_label} of the ${sample.date} edition, with every detected region outlined by content type`}
                className="block w-full"
              />
              {sample.regions.map((region, i) => (
                <RegionOutline key={i} region={region} />
              ))}
            </div>
          </div>
          <div className="ident mt-[--half] uppercase leading-relaxed">
            {sample.page_label} · {sample.date} · {sample.geometry} · {sample.regions.length}{' '}
            regions
            <br />
            The scan is never retouched, dimmed or inverted.
          </div>
        </div>
        <div className="pt-[--leading]">
          <h3 className="mb-[--leading] text-[clamp(24px,2.6vw,32px)] leading-[1.15] tracking-[-0.012em]">
            Every item has a physical address.
          </h3>
          <p className="mb-[--leading] max-w-[44ch] text-[16px] leading-[--leading] text-ink2">
            {sample.regions.length} regions on this front page. Each one carries its own record
            (headline, byline, section, word count) and its own coordinates, in the pixel space of
            the scan and in the points of the printed sheet.
          </p>
          <div className="flex flex-col gap-[--half] border-t border-rule pt-[--half] font-mono text-[13px] leading-normal">
            {sample.address.map(([label, value]) => (
              <div key={label} className="flex justify-between gap-[--half]">
                <span className="text-ink2">{label}</span>
                <span>{value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

/**
 * One region outlined over the scan.
 *
 * The geometry is the design's, and each number in it is load-bearing. An
 * unselected outline is 1.5px with no fill so dense newsprint stays readable
 * through it; the selection gets 2px of accent, a 16% wash (above that
 * newsprint stops being readable) and an inner paper hairline that lifts it
 * off the print. Structural regions are dashed neutral: present and clickable
 * but visually last. Tabs are suppressed below 3% of page height rather than
 * shrunk.
 */
export function RegionOutline({
  region,
}: {
  region: { type: string; x: number; y: number; w: number; h: number; selected?: boolean }
}) {
  // The block-to-type grouping lives in one place; this reads it rather than
  // keeping a second copy that can drift out of step with the overlay.
  const t = typeOf(region.type)
  const muted = t.muted
  const style: React.CSSProperties = {
    position: 'absolute',
    left: `${region.x * 100}%`,
    top: `${region.y * 100}%`,
    width: `${region.w * 100}%`,
    height: `${region.h * 100}%`,
    boxSizing: 'border-box',
    borderRadius: 3,
  }
  if (region.selected) {
    Object.assign(style, {
      border: '2px solid var(--jl-accent)',
      background: 'rgba(122,34,51,0.16)',
      outline: '1px solid rgba(243,242,242,0.85)',
      outlineOffset: '-4px',
    })
  } else {
    Object.assign(style, {
      border: `${muted ? '1px dashed ' : '1.5px solid '}${t.colour}`,
      background: muted ? 'transparent' : 'rgba(32,30,29,0.02)',
    })
  }
  return (
    <div style={style} aria-hidden="true">
      {region.h > 0.03 && (
        <div
          className="absolute -left-px -top-px px-[3px] font-mono text-[8px] leading-normal"
          style={{
            background: region.selected ? 'var(--jl-accent)' : t.colour,
            color: 'var(--color-bg)',
            borderRadius: '1px 0 1px 0',
          }}
        >
          {t.code}
        </div>
      )}
    </div>
  )
}

export function LandingFooter() {
  const { config } = useSession()
  return (
    <div className="flex flex-wrap justify-between gap-[--leading] border-t border-ink pt-[--leading]">
      {/*
        The design board carried a note here reading "a working name / the
        displayed name is set once, in the top-left of the chrome". That was
        addressed to whoever was reviewing the design, not to a visitor, and
        telling a prospective subscriber that the product's name is provisional
        is not a thing to print on the front of it.
      */}
      <div className="ident leading-loose">
        <div className="text-ink">{config?.product_name}</div>
        <div>A licensed research platform over digitised newspaper archives.</div>
      </div>
      <div className="flex gap-[--leading] text-[13px] uppercase tracking-[0.08em]">
        <a href="/docs/">Documentation</a>
        <a href="#access">Accessibility · WCAG 2.1 AA</a>
        <Link to="/rights">Rights holders</Link>
      </div>
    </div>
  )
}

/** The contents rail. Numbered like a front-page index, because it is one. */
const INDEX: [string, string, boolean?][] = [
  ['The page viewer, with every region outlined', '01', true],
  ['Search across the whole run in one field', '02'],
  ['Clippings, projects and citation export', '03'],
  ['How often a word appears, year by year', '04'],
  ['Browse an edition by date', '05'],
  ['Result lists as CSV, for citation managers', '06'],
]

const SECTION_OF = ['how', 'how', 'access', 'how', 'coverage', 'access']

const FEATURES: [string, string][] = [
  [
    'Provenance',
    'Nothing is presented without a route back to the page it sits on. Publication, date, page, section and region. Every one of them is a link, and the link resolves to the passage lit up on the original sheet. A result you cannot verify is not a result.',
  ],
  [
    'Honesty about doubt',
    'Text read from old newsprint is imperfect, and the interface says so. Every passage shows how sure the recognition was, the words it was unsure of are underlined, and the text as printed is kept apart from any correction. Nothing implies more certainty than exists.',
  ],
  [
    'Built to be cited',
    'A citation points at a passage on a page, not at a database row. Select the passage and its reference is composed for you, in the style your discipline uses. It is frozen the moment you save it, so later reprocessing cannot change what a published footnote points to.',
  ],
]

const TIERS = [
  {
    name: 'Guest',
    sub: 'No sign-in',
    gets: [
      'Headline, byline and date',
      'Section and page reference',
      'A short snippet',
      'Whether a result exists at all',
    ],
    not: ['Page image', 'Full text'],
  },
  {
    name: 'Reader',
    sub: 'Library access',
    gets: [
      'The page image, at full resolution',
      'The recognised text',
      'Clippings and citation export',
      'Browse any edition by date',
    ],
    not: ['Result-list export', 'API'],
  },
  {
    name: 'Researcher',
    sub: 'Sustained work',
    gets: [
      'Everything a reader has',
      'Result lists as CSV or JSON: headlines, dates and permalinks, never text or images',
      'The API',
      'Saved searches with alerts',
      'How often a word appears, year by year',
    ],
    not: [] as string[],
  },
]

const FACTS: [string, string][] = [
  ['Access models', 'Concurrent users, named users, or unlimited'],
  ['Scope', 'Per publication, date range and section'],
  ['Rights', 'Download and result-list export, set per licence'],
  ['Standards', 'COUNTER R5, SUSHI, OAI-PMH, MARC21, METS/ALTO'],
  ['Sign-in', 'SAML, EZproxy, IP ranges, or local accounts'],
  ['Trials', 'Time-boxed, with the same reporting as a licence'],
]
